import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Student as StudentIcon,
  ChalkboardTeacher,
  CalendarBlank,
  Exam,
  ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError, useAuth } from "../lib/api";
import AcademicDomainChart from "../components/AcademicDomainChart";
import EnamedProfilePanel from "../components/EnamedProfilePanel";
import StudentLearningTimeline from "../components/StudentLearningTimeline";
import StudentPortalPage from "./StudentPortalPage";

const formatDate = (value) => {
  if (!value) return "data não informada";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("pt-BR");
};

const scoreTone = (score) => {
  if (score >= 70) return "text-[hsl(var(--success))]";
  if (score >= 50) return "text-[hsl(var(--warning))]";
  return "text-destructive";
};

export default function StudentDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get(`/students/${id}`);
      setData(response.data);
    } catch (e) {
      const message = fmtError(e);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="max-w-[1400px] mx-auto px-8 py-16 label-eyebrow">carregando histórico do aluno…</div>;
  }

  if (!data) {
    return (
      <div className="max-w-[1400px] mx-auto px-8 py-16">
        <div className="label-eyebrow text-destructive">{error || "aluno não encontrado"}</div>
        <Link to="/students" className="text-primary text-sm font-mono-data hover:underline mt-4 inline-block">
          ← Voltar para alunos
        </Link>
      </div>
    );
  }

  if (user?.student_id || user?.account_type === "student") {
    return <StudentPortalPage data={data} />;
  }

  const {
    student,
    schools = [],
    classes = [],
    semesters = [],
    summary = {},
    performance_progression: progression = [],
    academic_history: academicHistory = [],
    academic_domains: academicDomains,
    external_engagement: externalEngagement,
  } = data;

  return (
    <div className="max-w-[1400px] mx-auto px-8 py-10">
      <Link
        to="/students"
        className="inline-flex items-center gap-2 label-eyebrow hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft size={14} weight="bold" /> Voltar aos alunos
      </Link>

      <div className="border-b border-border pb-7 mb-8">
        <div className="flex items-start gap-4">
          <StudentIcon size={34} weight="duotone" className="text-primary mt-1 shrink-0" />
          <div>
            <div className="label-eyebrow mb-2">histórico individual</div>
            <h1 className="font-display text-5xl font-black tracking-tighter leading-none">{student.name}</h1>
            <div className="font-mono-data text-sm text-muted-foreground mt-3">
              {student.cpf ? `CPF ${student.cpf}` : "Cadastro provisório · CPF não informado · sem acesso"}
            </div>
            {!!student.stamps?.length && (
              <div className="flex flex-wrap gap-2 mt-4" data-testid="student-stamps">
                {student.stamps.map((stamp) => (
                  <span key={stamp.id} className="border px-3 py-1 text-[11px] font-mono-data font-bold uppercase tracking-wider" style={{ borderColor: stamp.color, color: stamp.color }}>
                    {stamp.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-7 gap-3 mb-8">
        {[
          ["escolas", summary.schools || 0],
          ["turmas", summary.classes || 0],
          ["semestres com provas", summary.semesters || 0],
          ["provas realizadas", summary.exams || 0],
          ["cartões processados", summary.attempts || 0],
          ["média por acertos", `${Number(summary.average_score || 0).toFixed(1)}%`],
          ["média TRI", summary.average_tri_score == null ? "—" : Number(summary.average_tri_score).toFixed(1)],
        ].map(([label, value]) => (
          <div key={label} className="border border-border bg-card p-4">
            <div className="label-eyebrow mb-2">{label}</div>
            <div className="font-display text-3xl font-black">{value}</div>
          </div>
        ))}
      </div>

      <EnamedProfilePanel studentId={student.id} />

      <StudentLearningTimeline progression={progression} engagement={externalEngagement} />

      <section className="border border-border bg-card p-6 mb-8" data-testid="student-academic-history-summary">
        <div className="label-eyebrow mb-2">síntese acadêmica</div>
        <h2 className="font-display text-3xl font-black tracking-tight">Global, por semestre e disciplina.</h2>
        <p className="text-sm text-muted-foreground mt-2">A média global de cada semestre reúne todas as provas válidas; abaixo, as disciplinas e provas multidisciplinares permanecem discriminadas.</p>
        {!academicHistory.length ? (
          <div className="text-sm text-muted-foreground mt-5">Ainda não há resultados válidos para resumir.</div>
        ) : (
          <div className="grid gap-4 mt-5">
            {academicHistory.map((semester) => (
              <article key={semester.id} className="border border-border p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div><div className="label-eyebrow">semestre</div><div className="font-mono-data font-bold">{semester.name}</div></div>
                  <div className={`font-display text-3xl font-black ${scoreTone(semester.average_score)}`}>{Number(semester.average_score).toFixed(1)}%</div>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{semester.attempts} cartão(ões) com nota</div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 mt-4">
                  {semester.disciplines.map((discipline) => (
                    <div key={discipline.id} className="border border-border px-3 py-2">
                      <div className="text-xs text-muted-foreground truncate">{discipline.name}</div>
                      <div className={`font-mono-data font-black mt-1 ${scoreTone(discipline.average_score)}`}>{Number(discipline.average_score).toFixed(1)}%</div>
                      <div className="text-[11px] text-muted-foreground">{discipline.exams} prova(s) · {discipline.attempts} cartão(ões)</div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <AcademicDomainChart
        domains={academicDomains}
        eyebrow="mapa curricular individual"
        title="Desempenho por disciplina e conteúdo"
        description="Veja onde este aluno concentra acertos, erros e respostas em branco nas questões cadastradas das provas que realizou."
        individual
      />

      <div className="border border-border bg-card p-6 mb-4">
        <div className="label-eyebrow mb-4">escolas do aluno</div>
        {schools.length ? (
          <div className="flex flex-wrap gap-2">
            {schools.map((item) => (
              <div key={item.id} className="border border-primary/40 bg-primary/5 px-3 py-2 text-sm font-mono-data">
                {item.name}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Nenhuma escola acessível vinculada a este aluno.</div>
        )}
      </div>

      <div className="border border-border bg-card p-6 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <ChalkboardTeacher size={20} weight="duotone" className="text-primary" />
          <div className="label-eyebrow">turmas do aluno</div>
        </div>
        {classes.length ? (
          <div className="flex flex-wrap gap-2">
            {classes.map((item) => (
              <div key={item.id} className="border border-border px-3 py-2 text-sm font-mono-data">
                {item.name}
                {item.school_name ? <span className="text-muted-foreground"> · {item.school_name}</span> : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Nenhuma turma acessível vinculada a este aluno.</div>
        )}
      </div>

      <div className="mb-4">
        <div className="label-eyebrow mb-2">provas realizadas</div>
        <h2 className="font-display text-3xl font-black tracking-tight">Histórico por semestre e turma.</h2>
      </div>

      {!semesters.length ? (
        <div className="border border-border bg-card p-16 text-center">
          <Exam size={30} weight="duotone" className="text-muted-foreground mx-auto mb-3" />
          <div className="label-eyebrow">nenhuma prova vinculada a este aluno</div>
          <div className="text-sm text-muted-foreground mt-2">
            O histórico aparecerá aqui quando um cartão for processado com o CPF deste aluno dentro de uma prova.
          </div>
        </div>
      ) : (
        <div className="space-y-7">
          {semesters.map((semester) => (
            <section key={semester.id} className="border border-border bg-card">
              <div className="px-6 py-5 border-b border-border flex items-center justify-between gap-4 bg-muted/10">
                <div className="flex items-center gap-3">
                  <CalendarBlank size={22} weight="duotone" className="text-primary" />
                  <div>
                    <div className="label-eyebrow">semestre</div>
                    <div className="font-display text-2xl font-black tracking-tight">{semester.name}</div>
                  </div>
                </div>
                <div className="font-mono-data text-xs text-muted-foreground">
                  {semester.exam_count} prova(s)
                </div>
              </div>

              <div className="divide-y divide-border">
                {semester.classes.map((classGroup) => (
                  <div key={classGroup.id} className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <ChalkboardTeacher size={18} weight="duotone" className="text-primary" />
                      <div>
                        <div className="font-mono-data font-semibold">{classGroup.name}</div>
                        {classGroup.school_name ? (
                          <div className="text-xs text-muted-foreground">{classGroup.school_name}</div>
                        ) : null}
                      </div>
                    </div>

                    <div className="border border-border divide-y divide-border">
                      {classGroup.exams.map((examItem) => (
                        <div key={examItem.id} className="p-4">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
                            <div>
                              <div className="font-mono-data font-semibold">{examItem.name}</div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {examItem.exam_date ? `Realizada em ${new Date(`${examItem.exam_date}T12:00:00`).toLocaleDateString("pt-BR")} · ` : ""}
                                {examItem.attempt_count > 1 ? `${examItem.attempt_count} cartões processados` : "1 cartão processado"}
                              </div>
                            </div>
                            {examItem.latest_attempt && (
                              <div className="flex items-baseline gap-3 font-mono-data flex-wrap justify-end">
                                <span className="text-xs text-muted-foreground">
                                  {examItem.latest_attempt.hits}/{examItem.latest_attempt.total_valid} acertos
                                </span>
                                <span className={`text-xl font-black ${scoreTone(examItem.latest_attempt.score)}`}>
                                  {Number(examItem.latest_attempt.score).toFixed(1)}%
                                </span>
                                <span className="text-xl font-black text-primary">
                                  TRI {examItem.latest_attempt.tri_score == null ? "—" : Number(examItem.latest_attempt.tri_score).toFixed(1)}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            {examItem.attempts.map((attempt, index) => (
                              <Link
                                key={attempt.correction_id}
                                to={`/correction/${attempt.correction_id}?from=student&student_id=${student.id}`}
                                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border border-border px-4 py-3 hover:border-primary hover:bg-primary/5 transition-colors group"
                              >
                                <div>
                                  <div className="font-mono-data text-xs font-semibold">
                                    {examItem.attempt_count > 1 ? `Tentativa ${examItem.attempt_count - index}` : "Cartão da prova"}
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {formatDate(attempt.created_at)} · {attempt.filename || "arquivo sem nome"}
                                  </div>
                                </div>
                                <div className="flex items-center gap-4 shrink-0">
                                  <div className="text-right font-mono-data text-xs">
                                    <div>{attempt.hits} acertos · {attempt.misses} erros · {attempt.blank_valid} em branco</div>
                                    <div className={`font-black mt-1 ${scoreTone(attempt.score)}`}>Acertos: {Number(attempt.score).toFixed(1)}%</div>
                                    <div className="font-black text-primary mt-1">TRI: {attempt.tri_score == null ? "—" : Number(attempt.tri_score).toFixed(1)}</div>
                                    <div className={`mt-1 ${attempt.proficient === true ? "text-[hsl(var(--success))]" : attempt.proficient === false ? "text-destructive" : "text-muted-foreground"}`}>{attempt.proficiency_status || "TRI indisponível"}</div>
                                  </div>
                                  <ArrowRight size={16} weight="bold" className="text-muted-foreground group-hover:text-primary" />
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
